import React, { useState, useMemo, useCallback } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import type { Driver } from '@bringg/types';
import { Button, message, Spinner } from '@bringg/react-components';

import { useStores } from '../../../recipes';
import { TRANSLATION_DOMAINS } from '../../../consts';
import UserAssignerModal from '../../runs-assignment/user-assignment/user-assigner-modal';

import './styles.scss';

const { EXCEPTIONS_LIST: domain } = TRANSLATION_DOMAINS;

interface Props {
	taskId: number;
	driverId: number;
	isAllowedToAssignSpecificTask: boolean;
}

export const AssignModal = observer(({ taskId, driverId, isAllowedToAssignSpecificTask }: Props) => {
	const { t } = useTranslation();

	const {
		driversStore: { all: drivers, fetchByTeam, get: getDriverById },
		alertsStore
	} = useStores();

	const [userAssignerModalState, setUserAssignerModalState] = useState<{ open: boolean; driversByTeam: Driver[] }>({
		open: false,
		driversByTeam: []
	});
	const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);
	const [driversOfOrderTeams, setDriversOfOrderTeams] = useState([]);

	const task = alertsStore.getTasks.get(taskId);
	const driver = useMemo(() => {
		const driver = getDriverById(driverId);

		if (driver && !driversOfOrderTeams.find(teamDriver => teamDriver.id === driver.id)) {
			setDriversOfOrderTeams(driversOfOrderTeams.concat([driver]));
		}

		return driver;
	}, [drivers, driverId]);

	const buttonText = driverId ? driver?.name || t(`${domain}.UNKNOWN`) : t(`${domain}.UNASSIGNED`);

	const openAssignModal = async () => {
		const driversByTeam = await loadDrivers(task.team_ids);

		setUserAssignerModalState({ open: true, driversByTeam });
	};

	const handleAssignModalClose = useCallback(() => {
		setUserAssignerModalState({ open: false, driversByTeam: [] });
	}, []);

	// TODO: will be refactored to one solution of loading drivers for tasks
	// jira item: BIL-423
	const loadDrivers = async (teamIds: number[]) => {
		setIsLoadingDrivers(true);

		const driversByTeam: Driver[] = [];

		await Promise.all(teamIds.map(fetchByTeam))
			.then(res => {
				res.forEach(drivers => driversByTeam.push(...drivers));
			})
			.catch(() => message.error(t('ERRORS.ERROR_OCCURRED')));

		setIsLoadingDrivers(false);
		return driversByTeam;
	};

	return (
		<>
			<div className="assign-driver">
				<Button
					className="assign-driver-open-modal"
					type="link"
					onClick={openAssignModal}
					data-test-id="open-assign-modal"
					disabled={isLoadingDrivers || !isAllowedToAssignSpecificTask}
					title={buttonText}
				>
					{buttonText}
				</Button>
				{isLoadingDrivers && <Spinner />}
			</div>
			{userAssignerModalState.open && (
				<UserAssignerModal
					onClose={handleAssignModalClose}
					modalParams={{
						source: {
							tasks: [task],
							isAllRunWasSelected: false,
							selectedTasksWeight: { maxWeight: 0, weightCapacity: 0 }
						},
						readyToExecute: task.ready_to_execute,
						data: {
							drivers: userAssignerModalState.driversByTeam
						}
					}}
				/>
			)}
		</>
	);
});
